<script lang="ts">
    import { mdiTimer } from "@mdi/js"
    import { icons } from "../../../config"
    import { getRenderedComponent, tooltip } from "../../../helper"
    import { _ } from "svelte-i18n"
    import Icon from "../Icon.svelte"
    import TextField from "./TextField.svelte"
    import WeekField from "./WeekField.svelte"
    import TrackingListField from "./TrackingListField.svelte"
    import { login } from "../../../store"
    import type { SvelteComponent } from "svelte"
    import CustomStatus from "./CustomStatus.svelte"

    export let row: Operation | OperationOut
    // @ts-ignore
    if (!row) row = {}
    let statusSummary: OperationStatusSummary & OperationOutStatusSummary = row?.statusSummary || {},
        type = row?.type,
        inObjectionPeriodCount = 0,
        overObjectionPeriodCount = 0,
        inCount = 0,
        overCount = 0

    row?.positions
        ?.filter((p) => p.status.blockingIndicator != 2)
        .forEach((p) => {
            const code = p.status?.code || 0
            if (code >= 40 && code <= 44) {
                const _end = new Date(p.objectionPeriodEnd).getTime()
                if (_end > Date.now()) inCount++
                else overCount++
            } else if (code > 44) {
                overCount++
            }
        })
    inObjectionPeriodCount = inCount
    overObjectionPeriodCount = overCount

    let delayedConfirmation = false,
        delayedManufacture = false,
        delayedReadyToShip = false,
        plannedDateUnix: number,
        plannedWeek: string

    if (type == 2) {
        delayedConfirmation = false
        delayedManufacture = false
        delayedReadyToShip = false
        row?.positions
            ?.filter((p) => p.status.blockingIndicator != 2)
            .forEach((p) => {
                const planned = p.delivery?.plannedDate && new Date(p.delivery?.plannedDate).getTime()
                const now = Date.now()
                const status = p?.status?.code || 0
                if (status <= 91 && planned && planned < now) {
                    if (!plannedDateUnix || plannedDateUnix > planned) {
                        plannedDateUnix = planned
                        plannedWeek = p?.delivery?.plannedWeek
                    }
                    switch (true) {
                        case status < 40:
                            delayedConfirmation = true
                            break
                        case status < 90:
                            delayedManufacture = true
                            break
                        default:
                            delayedReadyToShip = true
                    }
                }
            })
    }

    let offerTooltip: string,
        orderedTooltip: string = "",
        emailedTooltip: string,
        orderConfimationEmailedTooltip: string,
        confirmTooltip: Tooltip = {
            content: $_("components.table.fields.status.orderConfirmation"),
        },
        readyToShipTooltip: Tooltip = {
            content: $_("components.table.fields.status.shipping"),
        },
        manufactureTooltip: Tooltip = {
            content: $_("components.table.fields.status.manufacture"),
        },
        statusSummaryTooltip,
        inShipmentTooltip,
        _text = "",
        objectionTooltip

    if ("created" in statusSummary && statusSummary?.created == 100) {
        offerTooltip = $_("components.table.fields.status.offerCreated")
    } else {
        offerTooltip = $_("components.table.fields.status.offerInProgress")
    }

    if ("emailed" in statusSummary && statusSummary?.emailed == 100) {
        const emailDetails =
            "emailSentTo" in row && "emailSentTimestamp" in row && row.emailSentTo && row.emailSentTimestamp
                ? $_("components.table.fields.status.emailDetails", {
                      values: {
                          recipient: row.emailSentTo,
                          timestamp: new Date(row.emailSentTimestamp).toLocaleString(),
                      },
                  })
                : ""

        emailedTooltip = $_("components.table.fields.status.offerEmailSent", {
            values: {
                emailDetails: emailDetails,
            },
        })
    } else {
        emailedTooltip = $_("components.table.fields.status.noOfferEmailSent")
    }

    if ("orderConfimationEmailed" in statusSummary && statusSummary?.orderConfimationEmailed == 100) {
        const emailDetails =
            "emailSentTo" in row && "emailSentTimestamp" in row && row.emailSentTo && row.emailSentTimestamp
                ? $_("components.table.fields.status.emailDetails", {
                      values: {
                          recipient: row.emailSentTo,
                          timestamp: new Date(row.emailSentTimestamp).toLocaleString(),
                      },
                  })
                : ""

        orderConfimationEmailedTooltip = $_("components.table.fields.status.orderConfirmationEmailSent", {
            values: {
                emailDetails: emailDetails,
            },
        })
    } else {
        orderConfimationEmailedTooltip = $_("components.table.fields.status.noOrderConfirmationEmailSent")
    }

    async function setConfirmTooltip() {
        if ("inConfirmCount" in statusSummary && statusSummary?.inConfirmCount) {
            confirmTooltip.content = $_("components.table.fields.status.inConfirmCount", {
                values: {
                    count: statusSummary.inConfirmCount,
                },
            })
        } else if ("confirmed" in statusSummary && statusSummary?.confirmed == 100) {
            confirmTooltip.content = $_("components.table.fields.status.orderConfirmed")
        } else {
            confirmTooltip.content = $_("components.table.fields.status.orderInProgress")
        }

        if (delayedConfirmation) {
            confirmTooltip.content = {
                component: TextField,
                props: {
                    value:
                        confirmTooltip.content +
                        `\n${$_("general.planned")}: ` +
                        (
                            await getRenderedComponent(WeekField as typeof SvelteComponent, {
                                value: plannedWeek,
                            })
                        ).innerText,
                },
            }
        }
    }
    setConfirmTooltip()

    if (inObjectionPeriodCount) {
        objectionTooltip = $_("components.table.fields.status.inObjectionPeriod", {
            values: {
                count: inObjectionPeriodCount,
            },
        })
    } else if (
        overObjectionPeriodCount < row.positions?.filter((p) => p.status.blockingIndicator != 2).length &&
        overObjectionPeriodCount
    ) {
        objectionTooltip = $_("components.table.fields.status.objectionPeriodExpiredForSome", {
            values: {
                count: overObjectionPeriodCount,
            },
        })
    } else if (overObjectionPeriodCount) {
        objectionTooltip = $_("components.table.fields.status.objectionPeriodExpired")
    } else {
        objectionTooltip = $_("components.table.fields.status.objectionPeriodAfterConfirmation")
    }

    async function setManufactureTooltip() {
        if ("inManufactureCount" in statusSummary && statusSummary?.inManufactureCount) {
            manufactureTooltip.content = $_("components.table.fields.status.inManufacture", {
                values: {
                    count: statusSummary.inManufactureCount,
                },
            })
        } else if ("manufactured" in statusSummary && statusSummary?.manufactured == 100) {
            manufactureTooltip.content = $_("components.table.fields.status.manufactureCompleted")
        } else {
            manufactureTooltip.content = $_("components.table.fields.status.noItemsInManufacture")
        }
        if (delayedManufacture) {
            manufactureTooltip.content = {
                component: TextField,
                props: {
                    value:
                        manufactureTooltip.content +
                        `\n${$_("general.planned")}: ` +
                        (
                            await getRenderedComponent(WeekField as typeof SvelteComponent, {
                                value: plannedWeek,
                            })
                        ).innerText,
                },
            }
        }
    }
    setManufactureTooltip()

    async function setReadyToShipTooltip() {
        if ("inReadyToShipCount" in statusSummary && statusSummary?.inReadyToShipCount) {
            readyToShipTooltip.content = $_("components.table.fields.status.inReadyToShip", {
                values: {
                    count: statusSummary.inReadyToShipCount,
                },
            })
        } else if ("readyToShip" in statusSummary && statusSummary?.readyToShip == 100) {
            readyToShipTooltip.content = $_("components.table.fields.status.readyToShipCompleted")
        } else {
            readyToShipTooltip.content = $_("components.table.fields.status.noItemsReadyToShip")
        }
        if (delayedReadyToShip) {
            readyToShipTooltip.content = {
                component: TextField,
                props: {
                    value:
                        readyToShipTooltip.content +
                        `\n${$_("general.planned")}: ` +
                        (
                            await getRenderedComponent(WeekField as typeof SvelteComponent, {
                                value: plannedWeek,
                            })
                        ).innerText,
                },
            }
        }
    }
    setReadyToShipTooltip()

    if ("inShipmentCount" in statusSummary && statusSummary.inShipmentCount) {
        _text =
            $_("components.table.fields.status.inShipment", {
                values: {
                    count: statusSummary.inShipmentCount,
                },
            }) + "\n"
    }

    if ("deliveredCount" in statusSummary && statusSummary.deliveredCount) {
        if (_text) _text += "\n"
        _text +=
            $_("components.table.fields.status.delivered", {
                values: {
                    count: statusSummary.deliveredCount,
                },
            }) + "\n"
    }

    if (!statusSummary?.inShipmentCount && !statusSummary?.deliveredCount) {
        _text = $_("components.table.fields.status.noItemsInShipment") + "\n"
    }

    if ("missingTracking" in statusSummary && statusSummary.missingTracking) {
        _text += "\n" + $_("components.table.fields.status.missingTracking")
    }
    inShipmentTooltip = _text

    if (row.positions.length === 1 && row.positions[0].status?.code > 40 && row.positions[0].tracking.length === 1) {
        statusSummaryTooltip = {
            content: {
                component: TrackingListField,
                props: {
                    position: row.positions[0],
                    doClose: null,
                    disabledLink: true,
                },
            },
            className: "tracking_overlay noclose",
        }
    } else statusSummaryTooltip = { content: inShipmentTooltip }

    if (row.type == 1)
        if (row.orderedSummary?.ordered) {
            orderedTooltip += $_("components.table.fields.status.orderedMultiple", {
                values: {
                    count: row.orderedSummary.orderedCount,
                },
            })
        } else {
            orderedTooltip += $_("components.table.fields.status.notOrdered")
        }
</script>

<div class="states">
    {#if type == 1}
        <span
            class="state {'created' in statusSummary && statusSummary?.created
                ? statusSummary.created == 100
                    ? 'green'
                    : 'yellow'
                : 'grey'}"
            use:tooltip="{{ content: offerTooltip }}"
        >
            <Icon path="{icons.offer}" size="14px" />
        </span>
        {#if "emailed" in statusSummary && typeof statusSummary?.emailed !== "undefined"}
            <!-- Ausgang -->
            <span
                class="state {statusSummary?.emailed ? (statusSummary.emailed == 100 ? 'green' : 'yellow') : 'grey'}"
                use:tooltip="{{ content: emailedTooltip }}"
            >
                <Icon path="{icons.email}" size="14px" />
            </span>
        {:else if row.orderedSummary || row.number > 6437718}
            <!-- Eingang -->
            <span
                class="state {row.orderedSummary?.ordered
                    ? row.orderedSummary.ordered == 100
                        ? 'green'
                        : 'split'
                    : 'grey'}"
                use:tooltip="{{
                    content: row.orderedSummary?.ordered
                        ? row.orderedSummary.ordered == 100
                            ? $_('components.table.fields.status.fullyOrdered')
                            : $_('components.table.fields.status.orderedCount', {
                                  values: {
                                      count: row.orderedSummary.orderedCount,
                                  },
                              })
                        : $_('components.table.fields.status.notOrdered'),
                }}"
                style="padding-left:5px; padding-top: 5px;"
                ><span class="left"></span><span class="right"></span>
                <Icon path="{icons.check}" size="20px" />
            </span>
            {#if $login?.account.roles?.includes("A") || $login?.account.permissions?.includes("O")}
                <span
                    class="state {'statusOutSummary' in row && row.statusOutSummary?.offerCreated == 100
                        ? 'green'
                        : 'grey'}"
                    use:tooltip="{{
                        content:
                            'statusOutSummary' in row && row.statusOutSummary?.offerCreated == 100
                                ? $_('components.table.fields.status.outboundOfferCreated')
                                : $_('components.table.fields.status.noOutboundOfferCreated'),
                    }}"
                    style="padding-left:5px; padding-top: 5px;"
                    ><span class="left"></span><span class="right"></span>
                    <Icon path="{icons.offerOut}" size="14px" viewBox="50 50 200 200" />
                </span>
            {/if}

            <!-- bestellt -->
        {/if}
    {:else if type == 2}
        <div
            class="state {delayedConfirmation
                ? 'red'
                : 'confirmed' in statusSummary
                  ? statusSummary.confirmed == 100
                      ? 'green'
                      : Number(statusSummary.confirmed) >= 60
                        ? 'split'
                        : 'yellow'
                  : 'grey'}"
            use:tooltip="{confirmTooltip}"
        >
            <span class="left"></span><span class="right"></span><span class="icon"
                ><Icon path="{icons.confirmation}" size="20px" viewBox="0 0 20 20" /></span
            >
        </div>
        <div
            class="state"
            class:grey="{!overObjectionPeriodCount && !inObjectionPeriodCount}"
            class:green="{overObjectionPeriodCount ===
                row?.positions?.filter((p) => p.status.blockingIndicator != 2).length}"
            class:yellow="{!!inObjectionPeriodCount && !overObjectionPeriodCount}"
            class:split="{overObjectionPeriodCount <
                row.positions?.filter((p) => p.status.blockingIndicator != 2).length && !!overObjectionPeriodCount}"
            use:tooltip="{{ content: objectionTooltip }}"
        >
            <span class="left"></span><span class="right"></span><span class="icon"><Icon path="{mdiTimer}" /></span>
        </div>
        <div
            class="state {delayedManufacture
                ? 'red'
                : 'manufactured' in statusSummary && statusSummary?.manufactured
                  ? statusSummary.manufactured == 100
                      ? 'green'
                      : Number(statusSummary.manufactured) >= 60
                        ? 'split'
                        : 'yellow'
                  : 'grey'}"
            use:tooltip="{manufactureTooltip}"
        >
            <span class="left"></span><span class="right"></span><span class="icon"
                ><Icon path="{icons.manufacture}" size="18px" viewBox="0 0 18 18" /></span
            >
        </div>
        <div
            class="state {delayedReadyToShip
                ? 'red'
                : 'readyToShip' in statusSummary && statusSummary?.readyToShip
                  ? statusSummary.readyToShip == 100
                      ? 'green'
                      : Number(statusSummary.readyToShip) >= 60
                        ? 'split'
                        : 'yellow'
                  : 'grey'}"
            use:tooltip="{readyToShipTooltip}"
        >
            <span class="left"></span><span class="right"></span><span class="icon"
                ><Icon path="{icons.readyToShip}" size="20px" viewBox="0 0 20 20" /></span
            >
        </div>
        <div
            class="state {'inShipment' in statusSummary && statusSummary?.inShipment
                ? statusSummary.inShipment == 100 || (statusSummary.readyToShip == 100 && statusSummary.missingTracking)
                    ? 'green'
                    : Number(statusSummary.inShipment) >= 60
                      ? 'split'
                      : 'yellow'
                : 'grey'}"
            use:tooltip="{statusSummaryTooltip}"
        >
            <span class="left"></span><span class="right"></span><span class="icon">
                <Icon path="{icons.shipping}" size="20px" viewBox="0 0 20 20" />
            </span>
            {#if "missingTracking" in statusSummary && statusSummary.missingTracking}
                <span class="redpoint"></span>
            {/if}
        </div>

        {#if row.collectionName == "operation-out"}
            <span
                class="state {'orderConfimationEmailed' in statusSummary && statusSummary?.orderConfimationEmailed
                    ? statusSummary.orderConfimationEmailed == 100
                        ? 'green'
                        : 'yellow'
                    : 'grey'}"
                use:tooltip="{{ content: orderConfimationEmailedTooltip }}"
            >
                <Icon path="{icons.orderConfirmationEmail}" size="14px" />
            </span>
        {:else if $login?.account.roles?.includes("A") || $login?.account.permissions?.includes("O")}
            <span
                class="state {row.statusOutSummary?.orderConfirmationCreated == 100 ? 'green' : 'grey'}"
                use:tooltip="{{
                    content:
                        row.statusOutSummary?.orderConfirmationCreated == 100
                            ? $_('components.table.fields.status.orderConfirmationCreated')
                            : $_('components.table.fields.status.noOrderConfirmationCreated'),
                }}"
                style="padding-left:5px; padding-top: 5px;"
                ><span class="left"></span><span class="right"></span>
                <Icon path="{icons.order}" size="14px" viewBox="50 50 200 200" />
            </span>
        {/if}
    {/if}
    {#if row.type == 1 && row.collectionName == "operation-out"}
        <span
            class="state {row.orderedSummary?.ordered ? 'green' : 'grey'}"
            use:tooltip="{{ content: orderedTooltip }}"
        >
            <Icon path="{icons.ordered}" size="14px" />
        </span>
    {/if}
    {#if $login?.account.roles?.includes("A") || $login?.account.roles?.includes("R")}
        <CustomStatus operation="{row}" type="Internal" />
    {/if}
    {#if $login?.account.roles?.includes("A") || $login?.account.roles?.includes("C")}
        <CustomStatus operation="{row}" type="Dealer" />
    {/if}
</div>
